/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';

import { getCustomerRecurringBooking, getCustomerBookings } from 'school-space-client/api';
import { useSteps } from 'school-space-client/hooks';
import {
    Divider,
    Spacer,
    Heading,
    UserContext,
    BookingStatus,
    Loading
} from 'school-space-client/components';
import { getTodaysDate, getYesterdaysDate } from 'school-space-utils/date';

import { showRecurringAmendments } from '../../config/features';
import { errorHandler } from '../../services';

import RecurringBookingSummary from '../components/sections/recurringBooking/RecurringBookingSummary';
import RecurringBookingSpaces from '../components/sections/recurringBooking/RecurringBookingSpaces';
import RecurringBookingActionButtons from '../components/sections/recurringBooking/RecurringBookingActionButtons';
import RecurringBookingsDatesList from '../components/sections/recurringBooking/RecurringBookingsDatesList';

import ProspectiveRecurringBooking from './ProspectiveRecurringBooking';

const css = {
    titleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }
};

const RecurringBooking = ({
    match: {
        params: { id }
    }
}) => {
    const { id: userId, showRebooking } = useContext(UserContext);
    const { stepIndex, nextStep } = useSteps(3);

    const [recurringBooking, setRecurringBooking] = useState(null);
    const [bookings, setBookings] = useState([]);

    const [loadingBookings, setLoadingBookings] = useState(false);
    const [showFutureDates, setShowFutureDates] = useState(true);

    const fetchRecurringBooking = useCallback(() => {
        setRecurringBooking(null);

        return getCustomerRecurringBooking(userId, id)
            .then(setRecurringBooking)
            .catch(error =>
                setRecurringBooking(() => {
                    throw error;
                })
            );
    }, [userId, id]);

    useEffect(() => {
        fetchRecurringBooking();
    }, [fetchRecurringBooking]);

    const fetchBookings = useCallback(() => {
        setLoadingBookings(true);

        return getCustomerBookings(userId, {
            recurringBookingId: id,
            ...(showFutureDates
                ? { fromDate: getTodaysDate() }
                : { toDate: getYesterdaysDate(), orderByOrder: 'desc' }),
            states: ['confirmed', 'amending', 'cancelled']
        })
            .then(setBookings)
            .catch(error => {
                errorHandler.report(error);
                setBookings([]);
            })
            .then(() => setLoadingBookings(false));
    }, [userId, id, showFutureDates]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    if (!recurringBooking) {
        return (
            <main>
                <div css={css.titleRow}>
                    <Heading level="1" size="x-large">
                        Repeat booking
                    </Heading>
                </div>
                <Loading />
            </main>
        );
    }

    const isProspective = recurringBooking.stateId === 'prospective';
    if (showRebooking && isProspective) {
        return (
            <ProspectiveRecurringBooking
                recurringBooking={recurringBooking}
                refetchBooking={fetchRecurringBooking}
                stepIndex={stepIndex}
                nextStep={nextStep}
            />
        );
    }

    const refetchPageData = () => Promise.all([fetchRecurringBooking(), fetchBookings()]);

    const isCancelled = recurringBooking.stateId === 'cancelled';
    const isEditable = showRecurringAmendments() && !isCancelled && !recurringBooking.isFinished;

    return (
        <main>
            <div css={css.titleRow}>
                <Heading level="1" size="x-large">
                    Repeat booking
                </Heading>
                {isCancelled && <BookingStatus state={recurringBooking.stateId} />}
            </div>
            <Spacer height={2} />
            <React.Fragment>
                <RecurringBookingSummary recurringBooking={recurringBooking} />
                <RecurringBookingSpaces
                    recurringBooking={recurringBooking}
                    refetchBooking={refetchPageData}
                    spacesAreEditable={isEditable}
                />
                {isEditable && (
                    <RecurringBookingActionButtons
                        recurringBooking={recurringBooking}
                        refetchBooking={refetchPageData}
                    />
                )}
                <Spacer height={2} />
                <Divider />
                <Spacer />
                <RecurringBookingsDatesList
                    bookings={bookings}
                    showFutureDates={showFutureDates}
                    refetchBookings={fetchBookings}
                    loading={loadingBookings}
                    toggleFutureDates={() => setShowFutureDates(!showFutureDates)}
                />
            </React.Fragment>
        </main>
    );
};

RecurringBooking.propTypes = {
    match: ReactRouterPropTypes.match.isRequired
};

export default RecurringBooking;
